import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Layout from "@/components/Layout";
import { api, fileUrl } from "@/lib/api";
import { MessageCircle, MapPin, Calendar, Store, ArrowLeft, CalendarDays } from "lucide-react";
import VerifiedBadge from "@/components/VerifiedBadge";
import SocialIcons from "@/components/SocialIcons";
import ContributorBadge from "@/components/ContributorBadge";
import PhotoLightbox from "@/components/PhotoLightbox";

export default function MemberDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [lightbox, setLightbox] = useState(false);

  useEffect(() => { (async () => {
    try { const { data } = await api.get(`/members/${id}`); setData(data); }
    catch (err) { console.error("Failed to load member:", err); }
  })(); }, [id]);

  if (!data) return <Layout><div className="text-bizma-subtext">Memuat...</div></Layout>;
  const m = data.member;

  return (
    <Layout>
      <Link to="/app" className="inline-flex items-center gap-2 text-sm text-bizma-subtext mb-6" data-testid="back-to-directory">
        <ArrowLeft size={16}/> Kembali ke Direktori
      </Link>
      <div className="grid lg:grid-cols-3 gap-6" data-testid="member-detail">
        <div className="felt-card p-6 lg:col-span-1">
          <div className="flex flex-col items-center text-center">
            {m.photo_path ? (
              <button type="button" onClick={() => setLightbox(true)} className="rounded-full focus:outline-none focus:ring-4 focus:ring-bizma-terracotta/40" data-testid="member-photo-btn" aria-label="Lihat foto ukuran penuh">
                <img src={fileUrl(m.photo_path)} alt={m.name} className="w-32 h-32 rounded-full object-cover ring-4 ring-bizma-sage/40 hover:ring-bizma-terracotta/60 transition-all cursor-zoom-in" />
              </button>
            ) : (
              <div className="w-32 h-32 rounded-full bg-bizma-sage text-white grid place-items-center text-4xl font-semibold">{m.name?.charAt(0)}</div>
            )}
            <h1 className="mt-4 text-2xl font-display font-bold flex items-center gap-2">
              {m.name}
              {m.is_verified && <VerifiedBadge size={20} />}
            </h1>
            {m.username && <div className="text-sm text-bizma-subtext">@{m.username}</div>}
            {m.kecamatan ? (
              <Link to={`/app/kecamatan/${encodeURIComponent(m.kecamatan)}`}
                className="mt-1 inline-flex items-center gap-1 text-sm text-bizma-sage font-medium hover:text-bizma-terracotta transition-colors"
                data-testid="member-kecamatan-link">
                <MapPin size={14}/> {m.kecamatan}, Magelang
              </Link>
            ) : (
              <div className="mt-1 inline-flex items-center gap-1 text-sm text-bizma-subtext font-medium"><MapPin size={14}/> Magelang</div>
            )}
            <ContributorBadge active={m.is_top_contributor} className="mt-2" />
            <SocialIcons socials={m.socials} className="mt-3 justify-center" />
            {m.whatsapp && (
              <a href={`https://wa.me/${m.whatsapp.replace(/\D/g, "")}`} target="_blank" rel="noreferrer"
                 className="mt-5 btn-wa w-full" data-testid="member-whatsapp-btn">
                <MessageCircle size={18} /> Chat WhatsApp
              </a>
            )}
            <div className="mt-5 w-full space-y-2 text-sm text-left">
              {m.bio && <div className="text-[#4A4743] leading-relaxed">{m.bio}</div>}
              {m.kecamatan && (
                <Link to={`/app/kecamatan/${encodeURIComponent(m.kecamatan)}`}
                  className="flex items-center gap-2 text-bizma-subtext hover:text-bizma-terracotta transition-colors"
                  data-testid="member-kecamatan-info-link">
                  <span className="text-bizma-sage"><MapPin size={14}/></span>
                  <span>{m.kecamatan}, Magelang</span>
                </Link>
              )}
              {m.birthdate && <InfoRow icon={<Calendar size={14}/>} value={new Date(m.birthdate).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" })} />}
            </div>
          </div>
        </div>

        <div className="lg:col-span-2 space-y-8">
          <div>
            <h2 className="font-display text-xl font-semibold mb-4 flex items-center gap-2"><Store size={20}/> Bisnis dari {m.name}</h2>
            {data.businesses.length === 0 ? (
              <div className="felt-card p-8 text-center text-bizma-subtext">Belum ada bisnis terdaftar.</div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-5">
                {data.businesses.map(b => (
                  <Link key={b.business_id} to={`/app/businesses/${b.business_id}`} className="felt-card overflow-hidden transition-transform hover:-translate-y-1" data-testid={`member-business-${b.business_id}`}>
                    <div className="aspect-video bg-bizma-muted">
                      {b.photo_path && <img src={fileUrl(b.photo_path)} alt={b.name} className="w-full h-full object-cover" />}
                    </div>
                    <div className="p-4">
                      <div className="text-xs uppercase tracking-widest text-bizma-sage font-bold">{b.category}</div>
                      <div className="font-display font-semibold mt-1">{b.name}</div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>

          <div>
            <h2 className="font-display text-xl font-semibold mb-4 flex items-center gap-2"><CalendarDays size={20}/> Event yang Dihadiri</h2>
            {(data.attended_events || []).length === 0 ? (
              <div className="felt-card p-8 text-center text-bizma-subtext" data-testid="attended-empty">Belum pernah mengikuti event.</div>
            ) : (
              <div className="felt-card divide-y divide-bizma-border" data-testid="attended-list">
                {(data.attended_events || []).map((ev) => {
                  const d = new Date(ev.date);
                  return (
                    <Link key={ev.event_id} to={`/app/events/${ev.event_id}`} className="flex items-center gap-4 p-4 hover:bg-bizma-muted transition-colors" data-testid={`attended-${ev.event_id}`}>
                      <div className="w-14 h-14 rounded-2xl bg-bizma-terracotta/10 text-bizma-terracotta grid place-items-center flex-col shrink-0">
                        <span className="text-[10px] uppercase font-bold leading-none">{d.toLocaleString("id-ID",{month:"short"})}</span>
                        <span className="text-xl font-display font-bold leading-none">{d.getDate()}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{ev.title}</div>
                        <div className="text-xs text-bizma-subtext truncate">{d.toLocaleDateString("id-ID",{year:"numeric",month:"long",day:"numeric"})}{ev.location?` • ${ev.location}`:""}</div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
      {lightbox && m.photo_path && <PhotoLightbox src={fileUrl(m.photo_path)} alt={m.name} onClose={() => setLightbox(false)} />}
    </Layout>
  );
}

function InfoRow({ icon, value }) {
  return <div className="flex items-center gap-2 text-bizma-subtext"><span className="text-bizma-sage">{icon}</span><span>{value}</span></div>;
}

// Render lightbox at root of MemberDetail return
